import React, { useMemo, useState, useEffect, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiSparkles } from 'react-icons/hi2';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import AuthContext from '../../context/AuthContext';
import SkuSelectionModal from '../../components/UI/SkuSelectionModal';
import { TITLE_MAX_LENGTH, ITEM_HIGHLIGHTS_MAX_LENGTH } from '../../utils/amazonValidation';
import useSingleFlight from '../../hooks/useSingleFlight';
import cardStyles from '../../components/UI/SelectProductCatalogueModal.module.css';
import styles from './UpdateTitleResultsPage.module.css';

const API_URL = process.env.REACT_APP_API_URL;
const RESULTS_PER_PAGE = 15;
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// The two generated fields are editable; Amazon caps each at a different length.
const FIELD_LIMITS = { newTitle: TITLE_MAX_LENGTH, highlight: ITEM_HIGHLIGHTS_MAX_LENGTH };
const FIELD_LABELS = { newTitle: 'New Title', highlight: 'New Item Highlight' };

// `currentColor` rather than a fixed fill so .editBtn drives the resting and hover
// colours; the resting colour is set to the intended #0752B0 in the stylesheet.
const EditIcon = () => (
    <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.3934 0.728785C19.9039 0.26103 19.253 0 18.5759 0C17.8989 0 17.2479 0.26103 16.7584 0.728785L14.9383 2.54629L7.39756 10.0871C7.06923 10.417 6.83596 10.8294 6.72232 11.2807L5.97253 14.2799C5.9088 14.5321 5.9033 14.7954 5.95645 15.05C6.0096 15.3046 6.12 15.5438 6.27928 15.7494C6.43856 15.955 6.64255 16.1217 6.8758 16.2368C7.10904 16.3518 7.36542 16.4123 7.6255 16.4136C7.76764 16.4135 7.90923 16.396 8.0471 16.3614L11.0463 15.6116C11.4982 15.4982 11.9107 15.2642 12.2399 14.9346L19.7807 7.39381L21.5991 5.57631C22.081 5.09423 22.3518 4.44047 22.3518 3.75881C22.3518 3.07715 22.081 2.42339 21.5991 1.94131L20.3934 0.728785ZM11.0326 13.7229C10.9229 13.8327 10.7855 13.9106 10.635 13.9483L7.63578 14.6989L8.38643 11.6998C8.42379 11.5491 8.50139 11.4113 8.61094 11.3013L15.5468 4.36636L17.9709 6.78969L11.0326 13.7229ZM20.3934 4.36379L19.18 5.57545L16.7559 3.15212L17.9684 1.94045C18.1315 1.78447 18.3485 1.69741 18.5742 1.69741C18.7999 1.69741 19.0169 1.78447 19.18 1.94045L20.3934 3.15212C20.5541 3.31281 20.6443 3.53073 20.6443 3.75795C20.6443 3.98517 20.5541 4.20309 20.3934 4.36379Z" fill="currentColor"/>
        <path d="M4.28454 22.2221H16.2812C17.4172 22.2207 18.5062 21.7689 19.3094 20.9656C20.1126 20.1624 20.5644 19.0734 20.5658 17.9375V9.89116C20.5658 9.6639 20.4755 9.44594 20.3148 9.28524C20.1541 9.12454 19.9361 9.03425 19.7089 9.03425C19.4816 9.03425 19.2636 9.12454 19.1029 9.28524C18.9422 9.44594 18.852 9.6639 18.852 9.89116V17.9375C18.852 18.6193 18.5811 19.2732 18.099 19.7553C17.6169 20.2374 16.963 20.5082 16.2812 20.5082H4.28454C3.60274 20.5082 2.94887 20.2374 2.46676 19.7553C1.98466 19.2732 1.71382 18.6193 1.71382 17.9375V5.94082C1.71382 5.25902 1.98466 4.60515 2.46676 4.12304C2.94887 3.64094 3.60274 3.3701 4.28454 3.3701H12.5451C12.7724 3.3701 12.9904 3.27981 13.1511 3.11911C13.3118 2.95841 13.402 2.74045 13.402 2.51319C13.402 2.28592 13.3118 2.06796 13.1511 1.90726C12.9904 1.74656 12.7724 1.65628 12.5451 1.65628H4.28454C3.14863 1.65764 2.05963 2.10948 1.25641 2.9127C0.453203 3.71591 0.00136065 4.80491 0 5.94082V17.9375C0.00136065 19.0734 0.453203 20.1624 1.25641 20.9656C2.05963 21.7689 3.14863 22.2207 4.28454 22.2221Z" fill="currentColor"/>
    </svg>
);

const PushIcon = () => (
    <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7.00031 11.8175C7.41088 11.8175 7.7432 11.4847 7.7432 11.0746V2.52743L11.1773 5.96206C11.3224 6.10717 11.5126 6.17948 11.7028 6.17948C11.893 6.17948 12.0832 6.10717 12.2283 5.96206C12.5185 5.67184 12.5185 5.20184 12.2283 4.91161L7.52578 0.209123C7.24695 -0.0697078 6.75367 -0.0697078 6.47533 0.209123L1.77284 4.91161C1.48262 5.20184 1.48262 5.67184 1.77284 5.96206C2.06306 6.25228 2.53307 6.25228 2.82329 5.96206L6.25742 2.52743V11.0746C6.25742 11.4847 6.58974 11.8175 7.00031 11.8175Z" fill="white"/>
        <path d="M0.743073 9.42444C0.332502 9.42444 0.000183105 9.75725 0.000183105 10.1673V13.6109C0.000183105 14.0209 0.332502 14.3538 0.743073 14.3538H13.2573C13.6679 14.3538 14.0002 14.0209 14.0002 13.6109V10.1673C14.0002 9.75725 13.6679 9.42444 13.2573 9.42444C12.8467 9.42444 12.5144 9.75725 12.5144 10.1673V12.868H1.48596V10.1673C1.48596 9.75725 1.15364 9.42444 0.743073 9.42444Z" fill="white"/>
    </svg>
);

// Results of an Update Title run: one card per ASIN comparing the old title against
// the generated 75-char title and the new item highlight. Usable as a route page
// (rows via location.state) or inline (rows + onBack via props, e.g. the Manage page).
const UpdateTitleResultsPage = ({ results: resultsProp = null, onBack = null }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showPushModal, setShowPushModal] = useState(false);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPushing, setIsPushing] = useState(false);
    const [pushStatus, setPushStatus] = useState(null);
    const [editing, setEditing] = useState(null);

    // Memoised so the derived hooks below don't recompute on every render — the
    // `|| []` fallback would otherwise hand them a new array reference each time.
    const sourceResults = useMemo(
        () => (resultsProp?.length ? resultsProp : (location.state?.results || [])),
        [resultsProp, location.state]
    );

    // Working copy so New Title / New Item Highlight can be edited in place. Rows carry
    // their own id because the same ASIN can appear under several SKUs, so the ASIN
    // alone is not a stable identity for edits or for React keys.
    const [rows, setRows] = useState([]);
    useEffect(() => {
        setRows(sourceResults.map((row, index) => ({ ...row, id: index })));
        setEditing(null);
    }, [sourceResults]);

    const handleBack = onBack || (() => navigate('/dashboard/projects'));

    const filteredResults = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return rows;
        return rows.filter(r =>
            [r.productTitle, r.oldTitle, r.newTitle, r.highlight, r.asin, r.sku]
                .some(field => field && field.toLowerCase().includes(query))
        );
    }, [rows, searchTerm]);

    // Reset to page 1 whenever the filtered set changes.
    useEffect(() => { setCurrentPage(1); }, [searchTerm, rows.length]);

    const totalPages = Math.max(1, Math.ceil(filteredResults.length / RESULTS_PER_PAGE));
    const pageStart = (currentPage - 1) * RESULTS_PER_PAGE;
    const pageResults = filteredResults.slice(pageStart, pageStart + RESULTS_PER_PAGE);

    // The selection modal keys on `sku`; for a title run the row identity is the ASIN.
    const selectableItems = useMemo(() => {
        const seen = new Set();
        return rows.reduce((acc, row) => {
            if (!row.asin || seen.has(row.asin)) return acc;
            seen.add(row.asin);
            acc.push({
                sku: row.asin,
                title: row.productTitle || row.newTitle || row.asin,
                thumbnail: row.image_url,
            });
            return acc;
        }, []);
    }, [rows]);

    const startEdit = (row, field) => setEditing({ id: row.id, field, value: row[field] || '' });
    const cancelEdit = () => setEditing(null);

    const commitEdit = () => {
        if (!editing || editing.value.length > FIELD_LIMITS[editing.field]) return;
        const { id, field, value } = editing;
        setRows(prev => prev.map(row => (row.id === id ? { ...row, [field]: value.trim() } : row)));
        setEditing(null);
    };

    const openDownloadModal = () => { setSelectedAsins(selectableItems.map(i => i.sku)); setShowDownloadModal(true); };
    const openPushModal = () => { setPushStatus(null); setSelectedAsins(selectableItems.map(i => i.sku)); setShowPushModal(true); };

    const handleToggleAsin = useCallback((asin) => {
        setSelectedAsins(prev => prev.includes(asin) ? prev.filter(a => a !== asin) : [...prev, asin]);
    }, []);
    const handleSelectAll = useCallback((asins) => { setSelectedAsins(asins); }, []);

    // One row per ASIN: the picker shows a single card per ASIN, so a catalogue that
    // repeats an ASIN across SKUs must not download twice or push the same ASIN twice.
    const selectedRows = useCallback(() => {
        const picked = new Set(selectedAsins);
        const taken = new Set();
        return rows.filter(row => {
            if (!picked.has(row.asin) || taken.has(row.asin)) return false;
            taken.add(row.asin);
            return true;
        });
    }, [rows, selectedAsins]);

    const handleDownloadConfirm = useCallback(() => {
        if (selectedAsins.length === 0) return;
        setIsDownloading(true);
        try {
            const sheetRows = selectedRows().map(row => ({
                ASIN: row.asin || '',
                'SKU ID': row.sku || '',
                'Old Title': row.oldTitle || '',
                'Old Title Characters': (row.oldTitle || '').length,
                'New Title': row.newTitle || '',
                'New Title Characters': (row.newTitle || '').length,
                'New Item Highlight': row.highlight || '',
                'Item Highlight Characters': (row.highlight || '').length,
            }));
            const worksheet = XLSX.utils.json_to_sheet(sheetRows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Title Updates');
            const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            saveAs(new Blob([buffer], { type: XLSX_MIME }), `title_updates_${sheetRows.length}_asins.xlsx`);
            setShowDownloadModal(false);
        } finally {
            setIsDownloading(false);
        }
    }, [selectedAsins, selectedRows]);

    // Pushes item_name + title_differentiation per ASIN. Sequential so a partial
    // failure is attributable and Amazon isn't hit with a burst of concurrent patches.
    const pushSelectedTitles = useCallback(async () => {
        const rows = selectedRows().filter(row => row.newTitle);
        if (rows.length === 0 || !authCtx.token) return;
        setIsPushing(true);
        const failed = [];
        let succeeded = 0;
        for (const row of rows) {
            try {
                const response = await fetch(`${API_URL}/oauth_amazon/spapi/update_amznProduct_title`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${authCtx.token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        asin: row.asin,
                        title: row.newTitle,
                        item_highlight: row.highlight || '',
                    }),
                });
                if (response.ok) succeeded += 1;
                else failed.push(row.asin);
            } catch {
                failed.push(row.asin);
            }
        }
        setIsPushing(false);
        setShowPushModal(false);
        setPushStatus(
            failed.length === 0
                ? { ok: true, message: `Pushed ${succeeded} ASIN${succeeded === 1 ? '' : 's'} to Amazon. Updates can take a few minutes to go live.` }
                : { ok: false, message: `Pushed ${succeeded} of ${rows.length}. Failed: ${failed.join(', ')}` }
        );
    }, [selectedRows, authCtx.token]);

    // A second click before the modal closes would re-PATCH every selected ASIN.
    const handlePushConfirm = useSingleFlight(pushSelectedTitles);

    const amazonConnected = Boolean(authCtx.user?.amazon_status);

    // New Title / New Item Highlight render as text with a pencil, and swap to a
    // textarea with a live counter while being edited. Saving is blocked over the limit.
    const renderEditableCell = (row, field) => {
        const limit = FIELD_LIMITS[field];
        const value = row[field] || '';

        if (editing && editing.id === row.id && editing.field === field) {
            const isOverLimit = editing.value.length > limit;
            return (
                <div className={styles.textCell}>
                    <textarea
                        className={styles.editInput}
                        value={editing.value}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') cancelEdit();
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitEdit(); }
                        }}
                        rows={3}
                        autoFocus
                        aria-label={`Edit ${FIELD_LABELS[field]} for ${row.asin}`}
                    />
                    <span className={isOverLimit ? styles.charNoteOver : styles.charNote}>
                        {editing.value.length}/{limit} characters
                        {isOverLimit ? ` — over the ${limit} character limit` : ''}
                    </span>
                    <div className={styles.editActions}>
                        <button type="button" className={styles.editSave} onClick={commitEdit} disabled={isOverLimit}>
                            Save
                        </button>
                        <button type="button" className={styles.editCancel} onClick={cancelEdit}>
                            Cancel
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.textCell}>
                <span className={styles.cellText}>{value || '—'}</span>
                <div className={styles.cellFooter}>
                    {value && (
                        <span className={value.length > limit ? styles.charNoteOver : styles.charNote}>
                            {value.length}/{limit} characters
                        </span>
                    )}
                    <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => startEdit(row, field)}
                        aria-label={`Edit ${FIELD_LABELS[field]} for ${row.asin}`}
                        title={`Edit ${FIELD_LABELS[field]}`}
                    >
                        <EditIcon />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className={styles.backRow}>
                <button type="button" className={styles.backBtn} onClick={handleBack} aria-label="Back to projects">
                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M25 13.75H9.7875L16.775 6.7625L15 5L5 15L15 25L16.7625 23.2375L9.7875 16.25H25V13.75Z" fill="#505050"/>
                    </svg>
                </button>
            </div>

            <div className={styles.page}>
                <div className={styles.topBar}>
                <div className={styles.resultsBadge}>
                    <HiSparkles className={styles.resultsBadgeIcon} />
                    <span>Showing Results for {rows.length} ASINs</span>
                </div>

                <div className={styles.searchBar}>
                    <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                    </svg>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search for Project Names, ASIN and SKU-IDs"
                        className={styles.searchInput}
                    />
                </div>
            </div>

            <div className={styles.actionsRow}>
                {pushStatus && (
                    <span className={pushStatus.ok ? styles.pushOk : styles.pushError}>{pushStatus.message}</span>
                )}
                <button
                    type="button"
                    className={styles.btnDownload}
                    onClick={openDownloadModal}
                    disabled={selectableItems.length === 0}
                >
                    Download Content
                </button>
                <button
                    type="button"
                    className={styles.btnPush}
                    onClick={openPushModal}
                    disabled={!amazonConnected || selectableItems.length === 0}
                    title={!amazonConnected ? 'Connect your Amazon account first' : ''}
                >
                    <PushIcon />
                    Push to Amazon
                </button>
            </div>

            <div className={styles.tableWrap}>
                <div className={styles.headerRow}>
                    <span>Product &amp; ASIN</span>
                    <span>Old Title</span>
                    <span>New Title</span>
                    <span>New Item Highlight</span>
                </div>

                {filteredResults.length === 0 ? (
                    <div className={styles.stateMsg}>
                        {rows.length === 0
                            ? 'No results to show. Open a title update from your projects.'
                            : `No results match "${searchTerm}"`}
                    </div>
                ) : (
                    pageResults.map((row) => (
                        <div key={row.id} className={styles.row}>
                            <div className={styles.productCell}>
                                <div className={styles.productImage}>
                                    {row.image_url && (
                                        <img
                                            src={row.image_url}
                                            alt={row.productTitle || row.asin}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    )}
                                </div>
                                <div className={styles.productInfo}>
                                    <span className={styles.productName}>{row.productTitle || row.asin}</span>
                                    <span className={styles.asinChip}>{row.asin}</span>
                                </div>
                            </div>

                            <div className={styles.textCell}>
                                <span className={styles.cellText}>{row.oldTitle || '—'}</span>
                                {row.oldTitle && <span className={styles.charNote}>{row.oldTitle.length} characters</span>}
                            </div>

                            {renderEditableCell(row, 'newTitle')}

                            {renderEditableCell(row, 'highlight')}
                        </div>
                    ))
                )}
            </div>

            {filteredResults.length > 0 && totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        type="button"
                        className={cardStyles.pageButton}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        <FaChevronLeft />
                    </button>
                    <div className={cardStyles.pageIndicator}>
                        <span>Page</span>
                        <input
                            type="number"
                            min={1}
                            max={totalPages}
                            value={currentPage}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (!value) return;
                                setCurrentPage(Math.min(totalPages, Math.max(1, value)));
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                            className={cardStyles.pageInput}
                            aria-label="Page number"
                        />
                        <span>of {totalPages}</span>
                    </div>
                    <button
                        type="button"
                        className={cardStyles.pageButton}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                    >
                        <FaChevronRight />
                    </button>
                </div>
            )}

            <SkuSelectionModal
                show={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                title="Download Content"
                buttonText="Download Content"
                iconType="download"
                skuList={selectableItems}
                selectedSkus={selectedAsins}
                onSkuToggle={handleToggleAsin}
                onSelectAll={handleSelectAll}
                onConfirm={handleDownloadConfirm}
                isConfirming={isDownloading}
            />

            <SkuSelectionModal
                show={showPushModal}
                onClose={() => setShowPushModal(false)}
                title="Push to Amazon"
                buttonText="Push to Amazon"
                iconType="push"
                skuList={selectableItems}
                selectedSkus={selectedAsins}
                onSkuToggle={handleToggleAsin}
                onSelectAll={handleSelectAll}
                onConfirm={handlePushConfirm}
                isConfirming={isPushing}
            />
            </div>
        </>
    );
};

export default UpdateTitleResultsPage;
